import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { CalendarDays, Hammer, Bell, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { WeekVisitsStrip } from "./WeekVisitsStrip";
import { DayRoutePlanner, defaultRouteDate } from "./DayRoutePlanner";

const fmtDate = (d) => new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
const monthKey = (d) => new Date(d).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

const TYPE_META = {
  start: { border: "border-[#C79A3B]", Icon: Hammer, iconCls: "text-[#C79A3B]" },
  site_visit: { border: "border-[#5B2A83]", Icon: MapPin, iconCls: "text-[#5B2A83]" },
  reminder: { border: "border-[#12263A]", Icon: Bell, iconCls: "" },
};

export function CalendarPanel({ onOpenBooking }) {
  const [events, setEvents] = useState([]);
  const [today, setToday] = useState("");
  const [weekStart, setWeekStart] = useState("");
  const [filter, setFilter] = useState("all");
  const [routeDate, setRouteDate] = useState("");

  useEffect(() => {
    api.get("/admin/calendar").then(r => {
      const evs = r.data.events || [];
      setEvents(evs); setToday(r.data.today || ""); setWeekStart(r.data.week_start || "");
      setRouteDate(defaultRouteDate(evs, r.data.today || ""));
    }).catch(() => {});
  }, []);

  const shown = events.filter(e => filter === "all" || e.type === filter);

  // group by month then date
  const groups = {};
  shown.forEach(e => {
    const mk = monthKey(e.date);
    (groups[mk] = groups[mk] || []).push(e);
  });

  return (
    <div data-testid="calendar-panel">
      <DayRoutePlanner events={events} today={today} date={routeDate} onChangeDate={setRouteDate} onOpenBooking={onOpenBooking} />
      <WeekVisitsStrip events={events} weekStart={weekStart} today={today} selectedDate={routeDate} onSelectDate={setRouteDate} onOpenBooking={onOpenBooking} />

      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex gap-2 flex-wrap">
          {[["all", "All"], ["site_visit", "Site visits"], ["start", "Build starts"], ["reminder", "Reminders"]].map(([v, l]) => (
            <button key={v} data-testid={`calendar-filter-${v}`} onClick={() => setFilter(v)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border ${filter === v ? "bg-[#12263A] text-white border-[#12263A]" : "border-[#E5E7EB] bg-white"}`}>{l}</button>
          ))}
        </div>
        <div className="flex gap-4 text-xs text-[#6B7280]">
          <span className="inline-flex items-center gap-1"><MapPin size={13} className="text-[#5B2A83]" /> Site visit</span>
          <span className="inline-flex items-center gap-1"><Hammer size={13} className="text-[#C79A3B]" /> Build start</span>
          <span className="inline-flex items-center gap-1"><Bell size={13} /> Reminder</span>
        </div>
      </div>

      {Object.keys(groups).length === 0 && (
        <div className="border border-dashed border-[#E5E7EB] p-10 text-center text-[#6B7280]">
          <CalendarDays size={24} className="mx-auto mb-3 opacity-50" /> Nothing scheduled in the next few months.
        </div>
      )}

      {Object.entries(groups).map(([mk, evs]) => (
        <div key={mk} className="mb-8">
          <div className="text-lg font-black tracking-tight mb-3 border-b border-[#E5E7EB] pb-1">{mk}</div>
          <div className="space-y-2">
            {evs.map((e, i) => <CalendarEvent key={`${e.date}-${e.type}-${i}`} e={e} i={i} today={today} onOpen={onOpenBooking} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function CalendarEvent({ e, i, today, onOpen }) {
  const meta = TYPE_META[e.type] || TYPE_META.reminder;
  const isToday = e.date === today;
  const isPast = e.date < today;
  const isVisit = e.type === "site_visit";
  return (
    <button data-testid={`calendar-event-${e.date}-${i}`} onClick={() => e.reference && onOpen?.(e.reference)}
      className={`w-full text-left flex items-center gap-4 border-l-4 ${meta.border} ${isVisit ? "bg-[#FBF8FD]" : "bg-white"} border-y border-r border-[#E5E7EB] p-3 hover:bg-[#FBF3EC] ${isPast ? "opacity-60" : ""}`}>
      <div className="w-20 shrink-0 text-center">
        <div className={`text-xs font-bold uppercase ${isToday ? "text-[#C79A3B]" : "text-[#6B7280]"}`}>{isToday ? "Today" : fmtDate(e.date).split(" ")[0]}</div>
        <div className="text-lg font-black leading-none">{new Date(e.date).getDate()}</div>
        <div className="text-[10px] text-[#6B7280]">{new Date(e.date).toLocaleDateString("en-GB", { month: "short" })}</div>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-bold flex items-center gap-2 flex-wrap">
          <meta.Icon size={14} className={`${meta.iconCls} shrink-0`} />
          {isVisit && e.time && <span className="inline-flex items-center gap-1 text-xs font-black text-[#5B2A83] bg-[#EADBF3] px-1.5 py-0.5" data-testid={`calendar-visit-time-${e.reference}`}><Clock size={11} /> {e.time}</span>}
          <span className="truncate">{e.title}</span>
          {isVisit && e.visit_status === "done" && <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#1F6B3B]"><CheckCircle2 size={11} /> Done</span>}
          {isVisit && e.source === "online" && <span className="text-[9px] font-black uppercase tracking-widest text-[#6B7280] border border-[#E5E7EB] px-1 py-0.5">Booked online</span>}
        </div>
        <div className="text-xs text-[#6B7280] truncate">
          {e.reference}{e.client_name && e.type === "reminder" ? ` · ${e.client_name}` : ""}
          {isVisit ? ` · ${e.address || e.postcode || "address TBC"}${e.phone ? ` · ${e.phone}` : ""}` : (e.postcode ? ` · ${e.postcode}` : "")}
        </div>
      </div>
    </button>
  );
}
